"use client";

import { useRef, useState, type CSSProperties } from "react";
import cnBind from "classnames/bind";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import type { Swiper as SwiperCore } from "swiper";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { ModalCallBack } from "@/components/_Modals/ModalCallBack";
import { useOptionalIndustryPreset } from "@/components/landing/IndustryPresetContext";
import { Routes } from "@/shared/constants";
import { useOnboardModal } from "@/shared/hooks/useOnboardModal";
import {
    BEAUTY_CAMPAIGN_SAVINGS,
    BEAUTY_CAMPAIGN_STARTER_MONTHLY,
    isCampaignOfferActive,
    remainingPlacesLabel,
    type PublicCampaign,
} from "@/shared/campaign/beautyCampaign";
import type { PublicSubscriptionPlan } from "@/shared/services/publicPlansApi";
import { isCommercialPlanCode } from "@/shared/services/publicPlansApi";
import type { TezamPlanCode } from "@/shared/types/onboard.types";
import { BynAmount } from "@/shared/ui/BynAmount";
import { Button } from "@/shared/ui/Button";
import { scrollToLeadForm } from "@/shared/utils/scrollToLeadForm";
import { toLeadIndustryPreset } from "@/shared/utils/toLeadIndustryPreset";
import { trackBeautyCampaignEvent } from "@/shared/utils/beautyCampaignAnalytics";
import { PRICING, SETUP_SERVICES, TRIAL_NOTE } from "@/view/Main/component/Tarify/const";
import { FALLBACK_PUBLIC_PLANS } from "@/view/Main/component/Tarify/fallbackPlans";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import styles from "./Tarify.module.scss";

const cx = cnBind.bind(styles);

export type TarifyProps = {
    eyebrow?: string | false;
    title?: string;
    subtitle?: string;
    showOffer?: boolean;
    showEnterprise?: boolean;
    ctaMode?: "modal" | "scroll";
    basicLabel?: string;
    fullLabel?: string;
    highlightedTariffId?: "start" | "business";
    nicheLabel?: string;
    nicheWantsPro?: boolean;
    nicheBadgeHint?: string;
    /** Live catalog from platform; falls back to static grid */
    plans?: PublicSubscriptionPlan[];
    showAllCards?: boolean;
    /** Разовые услуги «Наполнение под ключ» под сеткой тарифов */
    showSetupServices?: boolean;
    campaign?: PublicCampaign | null;
};

const DEFAULT_EYEBROW = "Telegram Mini App";
const DEFAULT_TITLE = "Два тарифа: Бизнес и Про";
const DEFAULT_SUBTITLE =
    "Mini App + CRM + сайт в одной подписке. 14 дней бесплатно. Цены в BYN. Спецтариф Альфа Банк — на странице тарифов.";

function toCard(plan: PublicSubscriptionPlan) {
    return {
        code: (isCommercialPlanCode(plan.code) ? plan.code : "demo") as TezamPlanCode,
        name: plan.name,
        label: plan.audienceLabel || plan.description || "",
        badge: plan.badge,
        featured: Boolean(plan.featured),
        amount: Number(plan.amount ?? 0),
        yearlyAmount: plan.yearlyAmount != null ? Number(plan.yearlyAmount) : undefined,
        period: plan.period || "мес",
        bullets: plan.bullets ?? [],
        cta: plan.ctaLabel || "Попробовать бесплатно",
        trialNote: TRIAL_NOTE,
    };
}

type TariffCardModel = ReturnType<typeof toCard>;

export const Tarify = ({
    eyebrow = DEFAULT_EYEBROW,
    title = DEFAULT_TITLE,
    subtitle = DEFAULT_SUBTITLE,
    showOffer = true,
    showEnterprise = false,
    ctaMode = "modal",
    basicLabel,
    fullLabel,
    highlightedTariffId,
    nicheLabel,
    nicheWantsPro = false,
    nicheBadgeHint,
    plans,
    showAllCards = false,
    showSetupServices = false,
    campaign,
}: TarifyProps = {}) => {
    const pathname = usePathname() ?? "/";
    const router = useRouter();
    const { isModalOpen, leadIntent, openSaasModal, openConsultationModal, closeModal } = useOnboardModal();
    const industry = useOptionalIndustryPreset();
    const beautyOffer =
        !industry?.isPlatform &&
        industry?.activePreset === "beauty" &&
        isCampaignOfferActive(campaign);
    const effectiveHighlight = beautyOffer ? "start" : highlightedTariffId;
    const featuredNicheSuffix = nicheBadgeHint
        ? ` · ${nicheBadgeHint}`
        : nicheWantsPro
          ? " · часто Про / СКУД"
          : "";
    const isTariffsPage = pathname === Routes.TARIFFS;
    const prevRef = useRef<HTMLButtonElement>(null);
    const nextRef = useRef<HTMLButtonElement>(null);
    const paginationRef = useRef<HTMLDivElement>(null);
    const [sliderLocked, setSliderLocked] = useState<boolean | null>(null);

    const source = plans && plans.length > 0 ? plans : FALLBACK_PUBLIC_PLANS;
    const ordered = [...source].sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100));
    const business = toCard(ordered.find((p) => p.code === "business") ?? FALLBACK_PUBLIC_PLANS[1]);
    const pro = toCard(ordered.find((p) => p.code === "pro") ?? FALLBACK_PUBLIC_PLANS[2]);
    /** Публичная сетка — два тарифа: Бизнес и Про (+ спецтариф Альфа на /tariffs). */
    const primaryCards = [business, pro];

    const handleSaasCta = (planCode: TezamPlanCode, planName: string) => {
        if (ctaMode === "scroll") {
            scrollToLeadForm(pathname, router.push);
            return;
        }
        if (beautyOffer && planCode === "start") {
            trackBeautyCampaignEvent("beauty_campaign_cta_click", campaign, { source_ui: "pricing" });
            trackBeautyCampaignEvent("beauty_campaign_signup_start", campaign, { source_ui: "pricing" });
            openSaasModal("start", "Beauty 100", {
                industryPreset: "BEAUTY",
                leadSource: "beauty_100_2026_pricing",
                campaignCode: campaign?.campaign || "BEAUTY_100_2026",
            });
            return;
        }
        if (beautyOffer && planCode !== "start") {
            trackBeautyCampaignEvent("beauty_campaign_upgrade_click", campaign, { plan: planCode });
        }
        openSaasModal(planCode, planName, {
            industryPreset: industry ? toLeadIndustryPreset(industry.activePreset) : undefined,
            leadSource: industry ? `homepage_tarify_${industry.activePreset}` : "tariffs_page",
        });
    };

    const bindSliderNav = (swiper: SwiperCore) => {
        const navigation = swiper.params.navigation;
        if (navigation && typeof navigation !== "boolean") {
            navigation.prevEl = prevRef.current;
            navigation.nextEl = nextRef.current;
        }
        const pagination = swiper.params.pagination;
        if (pagination && typeof pagination !== "boolean") {
            pagination.el = paginationRef.current;
        }
    };

    const syncSliderLock = (swiper: SwiperCore) => {
        setSliderLocked(Boolean(swiper.isLocked));
    };

    const renderCard = (card: TariffCardModel) => {
        const audience =
            card.code === "start"
                ? (basicLabel ?? card.label)
                : card.code === "business"
                  ? (fullLabel ?? card.label)
                  : card.label;

        return (
            <article
                key={card.code}
                className={cx("card", {
                    cardBasic: card.code === "start",
                    cardFeatured: card.featured || card.code === "business",
                    cardPro: card.code === "pro",
                    cardHolding: card.code === "holding",
                    cardNicheHit: effectiveHighlight === card.code,
                })}
            >
                <span className={cx("cardAccent")} aria-hidden />
                <div className={cx("cardBody")}>
                    <div className={cx("cardTop")}>
                        {effectiveHighlight === card.code && nicheLabel ? (
                            <span className={cx("badge", "badgeNiche")}>
                                Идеально для вашей ниши ({nicheLabel})
                                {card.code === "business" ? featuredNicheSuffix : ""}
                            </span>
                        ) : card.badge ? (
                            <span
                                className={cx("badge", {
                                    badgeHit: card.featured,
                                    badgeFlex: !card.featured,
                                })}
                            >
                                {card.badge}
                            </span>
                        ) : (
                            <span className={cx("badgeSpacer")} aria-hidden />
                        )}
                        <h3>{card.name}</h3>
                        <p>{audience}</p>
                    </div>
                    <div className={cx("priceLine")}>
                        {beautyOffer && card.code === "start" ? (
                            <>
                                <strong>0 BYN</strong>
                                <small>
                                    на 12 месяцев по программе BEAUTY 100
                                </small>
                                <small className={cx("priceStruck")}>
                                    <s>
                                        {BEAUTY_CAMPAIGN_STARTER_MONTHLY} BYN / месяц · {BEAUTY_CAMPAIGN_SAVINGS}{" "}
                                        BYN / год
                                    </s>
                                </small>
                                <small>
                                    {remainingPlacesLabel(campaign?.remaining ?? 0)}
                                </small>
                            </>
                        ) : (
                            <>
                                <strong>
                                    <BynAmount amount={card.amount} period={card.period} />
                                </strong>
                                {card.yearlyAmount ? (
                                    <small>
                                        или <BynAmount amount={card.yearlyAmount} /> за год
                                        {beautyOffer ? null : <> · {card.trialNote}</>}
                                    </small>
                                ) : beautyOffer ? null : (
                                    <small>{card.trialNote}</small>
                                )}
                            </>
                        )}
                    </div>
                    <ul className={cx("featureList")}>
                        {card.bullets.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </div>
                <Button
                    label={beautyOffer && card.code === "start" ? "Получить бесплатно" : card.cta}
                    variant={card.featured ? "solid" : "white"}
                    isFullWidth
                    iconTrue={false}
                    className={cx("cardAction")}
                    onClick={() => handleSaasCta(card.code, card.name)}
                />
            </article>
        );
    };

    return (
        <section className={cx("tarify", { tarifyCatalog: showAllCards })} id="tarify">
            <div className={cx("wrapper", "container")}>
                <div className={cx("head")}>
                    {eyebrow !== false ? <span>{eyebrow}</span> : null}
                    <h2>{title}</h2>
                    {subtitle && !beautyOffer ? <p className={cx("promoSubheadline")}>{subtitle}</p> : null}
                    {beautyOffer ? (
                        <p className={cx("promoSubheadline")}>
                            {campaign?.maxActivations ?? 100} Beauty-тенантов — TEZAM бесплатно на{" "}
                            {campaign?.freeMonths ?? 12} месяцев.{" "}
                            <strong>{remainingPlacesLabel(campaign?.remaining ?? 0)}</strong>. Без привязки карты.
                        </p>
                    ) : null}
                    {!isTariffsPage ? (
                        <p className={cx("tariffsLink")}>
                            <Link href={Routes.TARIFFS}>Полное сравнение тарифов →</Link>
                        </p>
                    ) : null}
                </div>

                {showOffer && !beautyOffer ? (
                    <div className={cx("offer")}>
                        <strong>14 дней бесплатно — полный доступ под вашу отрасль</strong>
                        <span>
                            Бизнес <BynAmount amount={business.amount || PRICING.business} period="мес" /> · Про{" "}
                            <BynAmount amount={pro.amount || PRICING.pro} period="мес" />. Скидка при оплате за год.
                        </span>
                    </div>
                ) : null}

                {showAllCards ? (
                    <div
                        className={cx("slider", { sliderLocked: sliderLocked === true })}
                        style={{ "--slides-count": primaryCards.length } as CSSProperties}
                    >
                        <div className={cx("sliderControls", { sliderControlsHidden: sliderLocked !== false })}>
                            <button
                                ref={prevRef}
                                type="button"
                                className={cx("sliderNav")}
                                aria-label="Предыдущий тариф"
                            >
                                <ChevronLeft size={20} strokeWidth={2.25} />
                            </button>
                            <div ref={paginationRef} className={cx("sliderDots")} />
                            <button
                                ref={nextRef}
                                type="button"
                                className={cx("sliderNav")}
                                aria-label="Следующий тариф"
                            >
                                <ChevronRight size={20} strokeWidth={2.25} />
                            </button>
                        </div>
                        <Swiper
                            className={cx("sliderSwiper")}
                            modules={[Navigation, Pagination, A11y]}
                            slidesPerView="auto"
                            spaceBetween={16}
                            watchOverflow
                            grabCursor
                            speed={480}
                            threshold={10}
                            resistanceRatio={0.65}
                            observer
                            observeParents
                            a11y={{
                                prevSlideMessage: "Предыдущий тариф",
                                nextSlideMessage: "Следующий тариф",
                            }}
                            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
                            pagination={{ clickable: true, el: paginationRef.current }}
                            onBeforeInit={bindSliderNav}
                            onSwiper={(swiper) => {
                                swiper.navigation?.init?.();
                                swiper.navigation?.update?.();
                                swiper.pagination?.init?.();
                                swiper.pagination?.update?.();
                                syncSliderLock(swiper);
                            }}
                            onLock={syncSliderLock}
                            onUnlock={syncSliderLock}
                            onResize={syncSliderLock}
                            onBreakpoint={syncSliderLock}
                        >
                            {primaryCards.map((card) => (
                                <SwiperSlide key={card.code} className={cx("slide")}>
                                    {renderCard(card)}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                ) : (
                    <div className={cx("saasGrid")}>{primaryCards.map((card) => renderCard(card))}</div>
                )}

                <aside className={cx("alfaTeaser")} aria-label="Спецтариф Альфа Банк">
                    <div>
                        <strong>Альфа Банк · 50 BYN / мес</strong>
                        <span>Специальный тариф для клиентов Альфа Банка</span>
                    </div>
                    <Link href={Routes.TARIFFS}>Подробнее →</Link>
                </aside>

                {showEnterprise ? (
                    <article className={cx("enterprise")}>
                        <div className={cx("enterpriseCopy")}>
                            <span className={cx("badge", "badgeEnterprise")}>Кастом</span>
                            <h3>Индивидуальный проект</h3>
                            <p>
                                Кастомная разработка и интеграции — по запросу, вне стандартной сетки тарифов.
                            </p>
                        </div>
                        <Button
                            label="Обсудить проект"
                            variant="solid"
                            iconTrue={false}
                            onClick={() => openSaasModal("holding", "Холдинг")}
                        />
                    </article>
                ) : null}

                {showSetupServices ? (
                    <article className={cx("setupServices")}>
                        <div className={cx("setupServicesCopy")}>
                            <span className={cx("badge", "badgeSetup")}>{SETUP_SERVICES.badge}</span>
                            <h3>{SETUP_SERVICES.title}</h3>
                            <p>{SETUP_SERVICES.description}</p>
                            <ul className={cx("setupServicesList")}>
                                {SETUP_SERVICES.items.map((item) => (
                                    <li key={`${item.label}-${item.amount}`}>
                                        <span>
                                            {item.label}
                                            {item.detail ? (
                                                <>
                                                    {" "}
                                                    <em>({item.detail})</em>
                                                </>
                                            ) : null}
                                        </span>
                                        <strong>
                                            <BynAmount amount={item.amount} />
                                        </strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <Button
                            label={SETUP_SERVICES.cta}
                            variant="solid"
                            iconTrue={false}
                            onClick={() =>
                                openConsultationModal(SETUP_SERVICES.title, "tariffs_setup_services")
                            }
                        />
                    </article>
                ) : null}
            </div>

            <ModalCallBack isOpen={isModalOpen} onClose={closeModal} leadIntent={leadIntent} />
        </section>
    );
};
